import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import Message from '../../models/Message'
import { compareDesc, isValid, parseISO } from 'date-fns'

import { medicareApiClient } from '../../clients/medicareApiClient'

import { Logger } from 'utils/logger'

// The body of every case reducer is implicitly wrapped with a call to
// `produce()` from the [immer](https://github.com/mweststrate/immer) library.
// This means that rather than returning a new state object, you can also
// mutate the passed-in state object directly; these mutations will then be
// automatically and efficiently translated into copies, giving you both
// convenience and immutability.
// CreateSlice uses CreateReducer which used immer produce

interface IMessageReducer {
  value: Array<Message>
  selectStatus?: 'finished' | 'error' | 'loading'
  fetchStatus?: 'finished' | 'error' | 'loading'
  error?: string
  selectedMessageId?: string,
  isMessageModalOpen: boolean;
}
interface IMessageGetter {
  messages: IMessageReducer
}

interface IMessageAction {
  messageIds: Array<string>,
  selectedMessageId?: string,
  isMessageModalOpen?: boolean;
}

export const fetchMessages = createAsyncThunk(
  'users/fetchUserWithMessages',
  async (language: string) => {
    try {
      return await medicareApiClient.asyncGetMessages(language)
    } catch (e) {
      Logger.error('Failed to load messages: ', e)
      throw e
    }
  }
)

export const markMessagesAsRead = createAsyncThunk(
  'messages/markMessagesAsRead',
  async (payload: IMessageAction, { dispatch }) => {
    // request update to messages on backend
    await medicareApiClient.asyncMarkMessagesAsRead(payload)
    // dispatch an action to update state
    dispatch(updateMessagesAsRead(payload))
  }
)

const INITAL_STATE: IMessageReducer = {
  value: [],
  selectStatus: undefined,
  fetchStatus: undefined,
  error: undefined, // TODO - implement
  selectedMessageId: undefined,
  isMessageModalOpen: false
}

export const messageSlice = createSlice({
  name: 'messages',
  initialState: INITAL_STATE,
  reducers: {
    // Modal
    closeMessageModal: (state) => {
      state.isMessageModalOpen = false
    },
    // dont set a selected message id if messageIds is longer than 1
    updateMessagesAsRead: (state, action: PayloadAction<IMessageAction>) => {
      const { messageIds } = action.payload
      const { value } = state
      const messages = value.map(item => ({
        ...item,
        ...(messageIds.includes(item.guid) ? { read: true } : {})
      }))

      state.value = messages
      state.selectedMessageId = action.payload.selectedMessageId ? action.payload.selectedMessageId : state.selectedMessageId
      state.isMessageModalOpen = action.payload.isMessageModalOpen ? action.payload.isMessageModalOpen : state.isMessageModalOpen
    },
    setSelectedMessageId: (state, action:PayloadAction<string>) => {
      state.selectedMessageId = action.payload
      state.isMessageModalOpen = true
    },
    clearSelectedMessageId: (state) => {
      state.selectedMessageId = undefined
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(markMessagesAsRead.pending, (state) => {
        state.selectStatus = 'loading'
      })
      .addCase(markMessagesAsRead.fulfilled, (state, { payload }) => { // what type is payload? -.-
        state.selectStatus = 'finished'
      })
      .addCase(markMessagesAsRead.rejected, (state) => {
        state.selectStatus = 'error'
      })
      .addCase(fetchMessages.pending, (state) => {
        state.fetchStatus = 'loading'
      })
      .addCase(fetchMessages.fulfilled, (state, { payload }: PayloadAction<Message[]>) => {
        const sortedMessages = payload.map(x => x).sort((a, b) => {
          const aCreatedOn = parseISO(a.createdOn)
          const bCreatedOn = parseISO(b.createdOn)
          const bothDatesAreValid = isValid(aCreatedOn) && isValid(bCreatedOn)

          if (bothDatesAreValid) {
            return compareDesc(aCreatedOn, bCreatedOn)
          }

          return 0
        })
        state.value = sortedMessages
        state.fetchStatus = 'finished'
      })
      .addCase(fetchMessages.rejected, (state:IMessageReducer) => {
        state.fetchStatus = 'error'
      })
  }
})

export const {
  // Messages
  updateMessagesAsRead,
  setSelectedMessageId,
  clearSelectedMessageId,
  closeMessageModal
} = messageSlice.actions

// GETTERS
export const getUnreadMessageCount = (state:IMessageGetter) => state.messages.value.filter(message => !message.read).length
export const getTotalMessageCount = (state:IMessageGetter) => state.messages.value.length
export const getAllMessages = (state:IMessageGetter) => state.messages.value
export const getSelectedMessageId = (state:IMessageGetter) => state.messages.selectedMessageId
export const getIsMessageModalOpen = (state:IMessageGetter) => state.messages.isMessageModalOpen

export default messageSlice.reducer
